import { gql } from '@apollo/client'
import { Button } from '@farol-ds/react'
import { ButtonKindEnum } from '@jusbrasil-web/shared-apollo-client/types'
import classNames from 'classnames'

import { useChooseFrequencyLaterEvents } from '../../../../hooks/use-choose-frequency-later-events'
import { CheckoutButton } from '../../../checkout-button'
import { OneClickButton } from '../../../one-click-button'
import { StepButtonLabels } from '../../types'
import { resolveOfferLockButton } from '../../utils'
import {
  ChooseFrequencyLaterPaymentFooter_OfferLockCardFragment,
  ChooseFrequencyLaterPaymentFooter_ViewerFragment,
} from './__generated__/choose-frequency-later-payment-footer.graphql'
import styles from './choose-frequency-later-payment-footer.module.scss'

interface ChooseFrequencyLaterPaymentFooterProps {
  offerLockCard: ChooseFrequencyLaterPaymentFooter_OfferLockCardFragment
  viewer: ChooseFrequencyLaterPaymentFooter_ViewerFragment
  isPaywall: boolean
  onBackBtnClick: () => void
}

export function ChooseFrequencyLaterPaymentFooter({
  offerLockCard,
  viewer,
  isPaywall,
  onBackBtnClick,
}: ChooseFrequencyLaterPaymentFooterProps) {
  const { metricsRef, sendReturnToOffersEvent, sendConfirmSubscriptionEvent } =
    useChooseFrequencyLaterEvents()

  const hasViewer = !!viewer
  const hasCreditCard = !!offerLockCard?.creditCard?.ccToken
  const forceCheckout = offerLockCard?.forceCheckout ?? false
  const shouldUseOneClick = hasCreditCard && hasViewer && !forceCheckout

  const buttons = offerLockCard?.buttons ?? []

  const confirm = resolveOfferLockButton(buttons, 1, {
    label: StepButtonLabels.CONFIRM,
    kind: ButtonKindEnum.Primary,
  })

  const back = resolveOfferLockButton(buttons, 2, {
    label: StepButtonLabels.BACK,
    kind: ButtonKindEnum.Tertiary,
  })

  const handleBackBtnClick = () => {
    offerLockCard && sendReturnToOffersEvent(offerLockCard)
    onBackBtnClick()
  }

  const handleConfirmSubscriptionClick = () => {
    offerLockCard && sendConfirmSubscriptionEvent(offerLockCard)
  }

  const footerClasses = classNames(styles.footer, { [styles.footerPaywall]: isPaywall })

  return (
    <div className={footerClasses}>
      <Button ref={metricsRef} kind={back.kind} onClick={handleBackBtnClick}>
        {back.label}
      </Button>

      {shouldUseOneClick ? (
        <OneClickButton
          offerLockCard={offerLockCard}
          viewer={viewer}
          kind={confirm.kind}
          customLabel={confirm.label}
          onClick={handleConfirmSubscriptionClick}
        />
      ) : (
        <CheckoutButton
          forceCheckout={forceCheckout}
          offerLockCard={offerLockCard}
          kind={confirm.kind}
          customLabel={confirm.label}
          viewer={viewer}
          onClick={handleConfirmSubscriptionClick}
        />
      )}
    </div>
  )
}

ChooseFrequencyLaterPaymentFooter.fragments = {
  offerLockCard: gql`
    fragment ChooseFrequencyLaterPaymentFooter_offerLockCard on OfferLockCard {
      forceCheckout
      buttons {
        label
        kind
      }
      ...UseChooseFrequencyLaterEvents_offerLockCard
      ...OneClickButton_offerLockCard
      ...CheckoutButton_offerLockCard
    }
    ${useChooseFrequencyLaterEvents.fragments.offerLockCard}
    ${OneClickButton.fragments.offerLockCard}
    ${CheckoutButton.fragments.offerLockCard}
  `,
  viewer: gql`
    fragment ChooseFrequencyLaterPaymentFooter_viewer on Profile {
      ...OneClickButton_viewer
    }
    ${OneClickButton.fragments.viewer}
  `,
}
